import { useCallback, useState } from "react";

/**
 * A boolean preference backed by localStorage — for the "flip it once"
 * kind of toggle (e.g. the Pool tab's "Not yet progressed only"). Returns
 * [value, setValue]. Storage failures (private mode, quota) are ignored and
 * the default applies.
 */
export function usePersistedFlag(
  storageKey: string,
  defaultValue = false,
): [boolean, (next: boolean) => void] {
  const [value, setValueState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "true") return true;
      if (stored === "false") return false;
      return defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (next: boolean) => {
      setValueState(next);
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        // Ignore write failures (quota exceeded, privacy mode)
      }
    },
    [storageKey],
  );

  return [value, setValue];
}
